/**
 * Vertrauenswürdiger Fakten-Resolver für den GuideEntryContext (Post 4444,
 * Issue #842/#845). Gegenstück zu `resolveEntryContext` im
 * VoiceAgentController: Die UI liefert NUR `source.kind` + `source.id` —
 * die Fakten für das Eröffnungs-Briefing kommen ausschließlich aus der
 * autoritativen ArTrack-Datenquelle (route_name, route_description,
 * total_km, kuratierte Stations-POIs). Kein einziges Wort aus der UI
 * erreicht die Instructions; das ist die Injection-Grenze des Vertrags.
 *
 * Aufgelöst werden `kind: 'route'` (ArTrack) und `kind: 'species'`
 * (knowledge-api, id = Knowledge-ID; seit 2026-10-06 für den Einstieg aus der
 * Arten-AR). Andere Kinds werfen — der Controller bricht den Open dann sauber
 * ab (fail-closed), statt ein leeres Briefing zu improvisieren.
 */

import { getConfig } from '../../config/project'
import {
  GUIDE_ENTRY_CONTEXT_PROTOCOL,
  type GuideEntryContext,
  type ResolvedGuideEntryContext,
} from './GuideEntryContext'

interface PrettyRoute {
  route_id: number
  route_name?: string
  route_description?: string
  total_km?: number
  pois?: Array<{ id: number; name: string; description: string | null; type: string; km: number }>
}

// Dieselben Kuratierungs-Regeln wie die Stationen-Liste in RouteDetailsView:
// nur manuelle Landmarken, keine Verkehrs-/Beschilderungspunkte.
const STATION_DROP_RE = /parkplatz|bushaltestelle|wegweiser|hubschrauberlandeplatz/i
const stripArticle = (s: string) => s.replace(/^\s*(das|der|die)\s+/i, '').trim()

const resolvedCache = new Map<string, ResolvedGuideEntryContext>()

async function fetchPrettyRoute(routeId: string): Promise<PrettyRoute> {
  const { artrackUrl, apiKey, trackId } = getConfig().api
  const res = await fetch(`${artrackUrl}/tracks/${trackId}/routes/${routeId}/pretty`, {
    headers: { 'X-API-KEY': apiKey },
  })
  if (!res.ok) throw new Error(`guide_entry_resolver:route_pretty_${res.status}`)
  const body = await res.json()
  return (body?.result ?? body) as PrettyRoute
}

function routeContextLine(route: PrettyRoute): string {
  const name = (route.route_name ?? '').replace(/^Route\s+/i, '').trim()
  const seen = new Set<string>()
  const stations: string[] = []
  for (const p of route.pois ?? []) {
    if (p.type !== 'manual' || !p.name || STATION_DROP_RE.test(p.name)) continue
    const key = stripArticle(p.name).toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    stations.push(`${stripArticle(p.name)} (km ${p.km.toFixed(1)})`)
  }
  const lines = [
    `Route: ${name || `#${route.route_id}`}${route.total_km ? ` — ${route.total_km.toFixed(1).replace('.', ',')} km` : ''}`,
    route.route_description ? `Beschreibung: ${route.route_description}` : '',
    stations.length > 0 ? `Stationen entlang des Wegs: ${stations.join(' · ')}` : '',
  ].filter(Boolean)
  // Der Vertrag deckelt context_line bei 6.000 Zeichen — hier defensiv
  // darunter bleiben, die Beschreibungen sind kuratiert aber lang.
  return lines.join('\n').slice(0, 5_000)
}

const KNOWLEDGE_API_FALLBACK = 'https://knowledge-api.arkturian.com'

interface AnnotatedSpecies {
  object_title?: string | null
  common_name_de?: string | null
  binom?: string | null
  object_description?: string | null
  short?: string | null
  annotations?: Array<{ label?: string | null; description?: string | null }>
}

async function fetchAnnotatedSpecies(knowledgeId: string, language: string): Promise<AnnotatedSpecies> {
  if (!/^\d{1,9}$/.test(knowledgeId)) throw new Error('guide_entry_resolver:invalid_species_id')
  const base = getConfig().api.knowledgeUrl ?? KNOWLEDGE_API_FALLBACK
  const res = await fetch(
    `${base}/api/v1/knowledge/annotated/${knowledgeId}?language=${encodeURIComponent(language)}`,
    { cache: 'no-store' },
  )
  if (!res.ok) throw new Error(`guide_entry_resolver:species_${res.status}`)
  return (await res.json()) as AnnotatedSpecies
}

/** Was der Besucher gerade in AR vor sich hatte: Art, Beschreibung und die
 *  Wissenspunkte am Modell — damit Tschauko an genau diese anknüpfen kann. */
export function speciesContextLine(species: AnnotatedSpecies): string {
  const title = (species.object_title || species.common_name_de || '').trim()
  const points = (species.annotations ?? [])
    .map((a, i) => {
      const label = (a.label ?? '').trim()
      if (!label) return ''
      const text = (a.description ?? '').trim()
      return `${i + 1}. ${label}${text ? ` — ${text}` : ''}`
    })
    .filter(Boolean)
  const lines = [
    `Art: ${title || 'unbekannt'}${species.binom ? ` (${species.binom})` : ''}`,
    'Der Besucher hat diese Art eben als 3D-Modell in Augmented Reality vor sich gehabt.',
    species.object_description ? `Beschreibung: ${species.object_description}` : (species.short ? `Kurz: ${species.short}` : ''),
    points.length > 0 ? `Wissenspunkte am Modell:\n${points.join('\n')}` : '',
  ].filter(Boolean)
  return lines.join('\n').slice(0, 5_000)
}

export async function resolveGuideEntryContext(
  entry: GuideEntryContext,
): Promise<ResolvedGuideEntryContext> {
  if (entry.source.kind === 'species') {
    const factsRef = `knowledge:species:${entry.source.id}:${entry.language}`
    const cached = resolvedCache.get(factsRef)
    if (cached) return cached
    const species = await fetchAnnotatedSpecies(entry.source.id, entry.language)
    const resolved: ResolvedGuideEntryContext = Object.freeze({
      protocol: GUIDE_ENTRY_CONTEXT_PROTOCOL,
      source: entry.source,
      facts_ref: factsRef,
      context_line: speciesContextLine(species),
    })
    resolvedCache.set(factsRef, resolved)
    return resolved
  }
  if (entry.source.kind !== 'route') {
    throw new Error(`guide_entry_resolver:unsupported_kind_${entry.source.kind}`)
  }
  const factsRef = `artrack:route:${entry.source.id}`
  const cached = resolvedCache.get(factsRef)
  if (cached) return cached
  const route = await fetchPrettyRoute(entry.source.id)
  const resolved: ResolvedGuideEntryContext = Object.freeze({
    protocol: GUIDE_ENTRY_CONTEXT_PROTOCOL,
    source: entry.source,
    facts_ref: factsRef,
    context_line: routeContextLine(route),
  })
  resolvedCache.set(factsRef, resolved)
  return resolved
}
